import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { checkInLocationService } from '@/api/domains/checkInLocation';
import type { CheckInLocationItem } from '@/api/domains/checkInLocation';
import { geographyService } from '@/api/domains/geography';
import type { Region, Commune } from '@/api/domains/geography';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { createLocationSchema, type CreateLocationFormData } from '@/schemas/checkInLocation/createLocationForm';


interface UseLocationFormPanelParams {
  record?: CheckInLocationItem;
  onSubmit?: () => void;
  readOnly?: boolean;
}

export interface UseLocationFormPanelResult {
  nameValue: string;
  addressValue: string;
  regionValue: string;
  communeValue: string;
  isRemoteWorkValue: boolean;
  regionOptions: { value: string; label: string }[];
  communeOptions: { value: string; label: string }[];
  isLoadingRegions: boolean;
  isLoadingCommunes: boolean;
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  isEditMode: boolean;
  errors: ReturnType<typeof useForm<CreateLocationFormData>>['formState']['errors'];
  isSubmitted: boolean;
  handleNameChange: (value: string) => void;
  handleAddressChange: (value: string) => void;
  handleRegionChange: (value: string | string[]) => void;
  handleCommuneChange: (value: string | string[]) => void;
  handleRemoteWorkChange: (checked: boolean) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
}

export function useLocationFormPanel({
  record,
  onSubmit,
  readOnly,
}: UseLocationFormPanelParams): UseLocationFormPanelResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showApiError, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  const effectiveReadOnly = !!readOnly;
  const isEditMode = !!record;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const preloadCommuneId = useRef<number | null>(
    isEditMode && record?.communeId != null ? record.communeId : null,
  );
  const preloadedRegionId = useRef<number | null>(null);

  // ── Local field state ──────────────────────────────────────────────────
  const [nameValue, setNameValue] = useState<string>(record?.name ?? '');
  const [addressValue, setAddressValue] = useState<string>(record?.address ?? '');
  const [regionValue, setRegionValue] = useState<string>('');
  const [communeValue, setCommuneValue] = useState<string>('');
  const [isRemoteWorkValue, setIsRemoteWorkValue] = useState<boolean>(record?.telework ?? false);

  // ── Geography catalog ──────────────────────────────────────────────────
  const [regions, setRegions] = useState<Region[]>([]);
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState<boolean>(true);
  const [isLoadingCommunes, setIsLoadingCommunes] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingRegions(true);

    void geographyService.getRegions('CL').then((response) => {
      if (cancelled) return;
      if (response.success && response.data) {
        setRegions(response.data);
      }
      setIsLoadingRegions(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setLoading(isLoadingRegions);
  }, [isLoadingRegions, setLoading]);

  // Edit mode: resolve which region contains the preloaded communeId
  useEffect(() => {
    if (!isEditMode || record?.communeId == null || regions.length === 0) return;

    let cancelled = false;
    const targetCommuneId = record.communeId;

    void Promise.all(
      regions.map((region) =>
        geographyService.getCommunes('CL', region.id).then((response) => ({
          regionId: region.id,
          communes: response.data ?? [],
        })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const found = results.find((result) =>
        result.communes.some((commune) => commune.id === targetCommuneId),
      );
      if (found) {
        preloadCommuneId.current = targetCommuneId;
        preloadedRegionId.current = found.regionId;
        setCommunes(found.communes);
        setRegionValue((current) => (current === '' ? String(found.regionId) : current));
      }
    });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions]);

  // Edit mode: once the cascade finishes loading communes, preselect the commune
  useEffect(() => {
    if (preloadCommuneId.current === null || isLoadingCommunes || communes.length === 0) return;
    const target = preloadCommuneId.current;
    if (communes.some((commune) => commune.id === target)) {
      setCommuneValue(String(target));
      preloadCommuneId.current = null;
    }
  }, [communes, isLoadingCommunes]);

  // Cascade: load communes when region changes
  useEffect(() => {
    if (!regionValue) {
      setCommunes([]);
      setCommuneValue('');
      setIsLoadingCommunes(false);
      return;
    }

    // Reuse communes already fetched by the preload effect to avoid a duplicate request
    const regionId = Number(regionValue);
    if (preloadedRegionId.current === regionId) {
      preloadedRegionId.current = null;
      setIsLoadingCommunes(false);
      return;
    }

    let cancelled = false;
    setIsLoadingCommunes(true);

    void geographyService.getCommunes('CL', regionId).then((response) => {
      if (cancelled) return;
      if (response.success && response.data) {
        setCommunes(response.data);
      }
      setIsLoadingCommunes(false);
    });

    return () => {
      cancelled = true;
      setIsLoadingCommunes(false);
    };
  }, [regionValue]);

  const regionOptions = useMemo(
    () => regions.map((region) => ({ value: String(region.id), label: region.name })),
    [regions],
  );

  const communeOptions = useMemo(
    () => communes.map((commune) => ({ value: String(commune.id), label: commune.name })),
    [communes],
  );

  // ── React Hook Form ────────────────────────────────────────────────────
  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useForm<CreateLocationFormData>({
    resolver: zodResolver(createLocationSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: record?.name ?? '',
      address: record?.address ?? '',
      communeId: null,
      isRemoteWork: record?.telework ?? false,
    },
  });

  useEffect(() => {
    register('name');
    register('address');
    register('communeId');
    register('isRemoteWork');
    return () => {
      unregister('name');
      unregister('address');
      unregister('communeId');
      unregister('isRemoteWork');
    };
  }, [register, unregister]);

  // Sync local state → form values
  useEffect(() => { setValue('name', nameValue); }, [nameValue, setValue]);
  useEffect(() => { setValue('address', addressValue); }, [addressValue, setValue]);
  useEffect(() => {
    setValue('communeId', communeValue ? Number(communeValue) : null);
    if (!regionValue) setValue('communeId', null);
  }, [communeValue, regionValue, setValue]);
  useEffect(() => { setValue('isRemoteWork', isRemoteWorkValue); }, [isRemoteWorkValue, setValue]);

  // ── isReady ────────────────────────────────────────────────────────────
  const isReady = useMemo(
    () => !isLoadingRegions && nameValue.trim().length > 0 && addressValue.trim().length > 0,
    [isLoadingRegions, nameValue, addressValue],
  );

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleNameChange = useCallback((value: string) => {
    setNameValue(value);
  }, []);

  const handleAddressChange = useCallback((value: string) => {
    setAddressValue(value);
  }, []);

  const handleRegionChange = useCallback((value: string | string[]) => {
    const next = Array.isArray(value) ? (value[0] ?? '') : value;
    setRegionValue(next);
    setCommuneValue('');
  }, []);

  const handleCommuneChange = useCallback((value: string | string[]) => {
    setCommuneValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleRemoteWorkChange = useCallback((checked: boolean) => {
    setIsRemoteWorkValue(checked);
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────
  const onValidSubmit = useCallback(
    async (values: CreateLocationFormData) => {
      if (effectiveReadOnly) return;
      setIsSubmitting(true);

      try {
        const params = {
          name: values.name,
          address: values.address,
          communeId: values.communeId ?? null,
          isRemoteWork: values.isRemoteWork,
        };

        const response = isEditMode && record
          ? await checkInLocationService.update({ id: record.id, ...params })
          : await checkInLocationService.create(params);

        if (!response.success) {
          if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
            injectValidationErrors(response.error.validationErrors, { setError });
            return;
          }
          const errorKey = isEditMode
            ? 'checkInLocation:form.messages.updateError'
            : 'checkInLocation:form.messages.createError';
          showApiError(response, translate(errorKey));
          return;
        }

        const successKey = isEditMode
          ? 'checkInLocation:form.messages.updateSuccess'
          : 'checkInLocation:form.messages.createSuccess';
        showSuccess(translate(successKey));
        closeSidePanel();
        onSubmit?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      effectiveReadOnly, isEditMode, record,
      setError, showApiError, showSuccess, closeSidePanel, onSubmit, translate,
    ],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      void rhfHandleSubmit(onValidSubmit)(event as unknown as React.BaseSyntheticEvent);
    },
    [rhfHandleSubmit, onValidSubmit],
  );

  return {
    nameValue,
    addressValue,
    regionValue,
    communeValue,
    isRemoteWorkValue,
    regionOptions,
    communeOptions,
    isLoadingRegions,
    isLoadingCommunes,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    isEditMode,
    errors,
    isSubmitted,
    handleNameChange,
    handleAddressChange,
    handleRegionChange,
    handleCommuneChange,
    handleRemoteWorkChange,
    handleSubmit,
  };
}
